import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SchoolServiceAdmin from '../services/SchoolServiceAdmin';
import { levelColors, levelTranslations } from './ConstFuncs';
import AdminDisplayTeacherAttendanceCheck from './AfterLoginPages/AdminDisplayTeacherAttendanceCheck';

import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

const formatDate = (value) =>
  value ? new Date(value).toLocaleString('de-DE') : 'Never sent';

const daysText = (days) => (days || []).join(', ');

const getTeacherName = (student) => {
  if (student.teacher) {
    return `${student.teacher.firstName || ''} ${student.teacher.lastName || ''}`.trim();
  }
  return student.teacherFirstName || '';
};

const getTeacherUsername = (student) =>
  student.teacher?.username || student.teacherUserName || '';

const getStudentDays = (student) =>
  student.teacher?.teacherClassDaysOfWeek ||
  student.classDaysOfWeek ||
  [];

const getNextBiWeeklyReportDate = () => {
  const startDate = new Date(2024, 4, 5);
  const today = new Date();
  const toMidnight = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  let candidate = toMidnight(today);
  const daysUntilSunday = (7 - candidate.getDay()) % 7;
  if (daysUntilSunday !== 0)
    candidate.setDate(candidate.getDate() + daysUntilSunday);
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  while (true) {
    const weeksBetween = Math.floor(
      (candidate - toMidnight(startDate)) / weekMs,
    );
    if (weeksBetween % 2 === 0) {
      return candidate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    candidate = new Date(candidate.getTime() + weekMs);
  }
};

const getTeacherLoadMood = (count) => {
  if (count === 0)
    return {
      label: 'No students yet',
      ring: 'ring-slate-200',
      chip: 'bg-slate-100 text-slate-600',
    };
  if (count <= 4)
    return {
      label: 'Small group',
      ring: 'ring-emerald-200',
      chip: 'bg-emerald-100 text-emerald-700',
    };
  if (count <= 8)
    return {
      label: 'Active class',
      ring: 'ring-cyan-200',
      chip: 'bg-cyan-100 text-cyan-700',
    };
  if (count <= 12)
    return {
      label: 'Busy class',
      ring: 'ring-amber-200',
      chip: 'bg-amber-100 text-amber-700',
    };
  return {
    label: 'Heavy load',
    ring: 'ring-rose-200',
    chip: 'bg-rose-100 text-rose-700',
  };
};

const StudentCountLines = ({ count }) => {
  const lineCount = Math.min(Math.max(count, 1), 18);
  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-slate-400">
        <span>Student load signal</span>
        <span>{count} assigned</span>
      </div>
      <div className="flex h-14 items-end gap-1 rounded-2xl bg-white p-3 ring-1 ring-slate-200">
        {Array.from({ length: lineCount }).map((_, i) => (
          <span
            key={i}
            className="w-full rounded-full bg-cyan-700/80"
            style={{ height: `${18 + (i % 5) * 14}%` }}
          />
        ))}
        {count === 0 && (
          <span className="text-xs font-bold text-slate-400">
            No assigned students
          </span>
        )}
      </div>
    </div>
  );
};

const TeacherAttendanceMonthlyChart = ({ data, attendanceRange }) => {
  const chartData = {
    labels: data.map(
      (item) => `${item.classAbbrev} • ${item.teacherName}`,
    ),
    datasets: [
      {
        label: 'Present records last month',
        data: data.map((item) => item.presentCount),
        backgroundColor: 'rgba(14, 116, 144, 0.75)',
        borderColor: 'rgba(14, 116, 144, 1)',
        borderWidth: 1,
        borderRadius: 12,
        barThickness: 34,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#0f172a',
        titleColor: '#ffffff',
        bodyColor: '#cffafe',
        padding: 12,
        cornerRadius: 12,
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#64748b',
          font: {
            size: 11,
            weight: 'bold',
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#64748b',
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.2)',
        },
      },
    },
  };

  return (
    <div className="rounded-3xl border border-cyan-100 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-black text-slate-900">
            Last Month Attendance
          </h2>

          <p className="text-sm font-semibold text-slate-400">
            Present records by class and teacher
          </p>
        </div>

        <div className="rounded-2xl border border-cyan-100 bg-gradient-to-r from-cyan-50 to-white px-4 py-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-600">
            Date Range
          </div>

          <div className="mt-1 text-sm font-bold text-slate-700">
            {attendanceRange.start}
            <span className="mx-2 text-cyan-500">→</span>
            {attendanceRange.end}
          </div>
        </div>
      </div>

      <div className="h-[360px] w-full overflow-x-auto">
        <div
          className="h-full"
          style={{
            minWidth: `${Math.max(data.length * 120, 700)}px`,
          }}
        >
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

const StudentAttendanceByClassChart = ({ data, attendanceRange }) => {
  const groupedByClass = data.reduce((acc, student) => {
    const classKey = `${student.classAbbrev} • ${student.teacherName} • @${student.teacherUsername}`;
    if (!acc[classKey]) acc[classKey] = [];

    acc[classKey].push(student);

    return acc;
  }, {});

  return (
    <div className="rounded-3xl border border-cyan-100 bg-white p-5 shadow-sm">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-black text-slate-900">
            Student attendance leaderboard
          </h2>
          <p className="text-sm font-semibold text-slate-400">
            All students grouped by class and teacher — last month
          </p>
        </div>

        <div className="rounded-2xl bg-cyan-50 px-4 py-2 text-sm font-black text-cyan-700">
          {data.length} students
        </div>
        <div className="rounded-2xl border border-cyan-100 bg-white px-4 py-2 text-sm font-bold text-slate-600">
          {attendanceRange.start} → {attendanceRange.end}
        </div>
      </div>

      <div className="space-y-8">
        {Object.entries(groupedByClass).map(
          ([className, students]) => {
            const maxPresent = Math.max(
              ...students.map((student) => student.presentCount),
              1,
            );

            return (
              <div
                key={className}
                className="rounded-3xl bg-slate-50 p-4 ring-1 ring-slate-200"
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h3 className="font-black text-cyan-800">
                    {className}
                  </h3>

                  <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-500 ring-1 ring-slate-200">
                    {students.length} students
                  </span>
                </div>

                <div className="space-y-3">
                  {students
                    .sort((a, b) => b.presentCount - a.presentCount)
                    .map((student, index) => {
                      const width =
                        (student.presentCount / maxPresent) * 100;

                      return (
                        <div
                          key={student.id}
                          className="rounded-2xl bg-white p-3 ring-1 ring-slate-100"
                        >
                          <div className="mb-2 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div
                                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-black ${
                                  index === 0
                                    ? 'bg-cyan-700 text-white'
                                    : 'bg-cyan-50 text-cyan-700'
                                }`}
                              >
                                #{index + 1}
                              </div>

                              <p className="font-bold text-slate-800">
                                {student.studentName}
                              </p>
                            </div>

                            <p className="text-sm font-black text-cyan-700">
                              {student.presentCount} present
                            </p>
                          </div>

                          <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-cyan-700 to-teal-400"
                              style={{ width: `${width}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
);

const SchoolAdmin = ({ data }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allStudents, setAllStudents] = useState([]);
  const [allTeachers, setAllTeachers] = useState([]);
  const [allArchivedStudents, setAllArchivedStudents] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [teacherAttendanceStats, setTeacherAttendanceStats] =
    useState([]);

  const [studentAttendanceStats, setStudentAttendanceStats] =
    useState([]);

  const [attendanceRange, setAttendanceRange] = useState({
    start: '',
    end: '',
  });

  useEffect(() => {
    if (!data) {
      localStorage.clear();
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [students, teachers, archived] =
          await Promise.allSettled([
            SchoolServiceAdmin.getAllStudents(),
            SchoolServiceAdmin.getAllTeachers(),
            SchoolServiceAdmin.getAllArchivedStudents?.(),
          ]);

        if (students.status === 'fulfilled')
          setAllStudents(students.value.data || []);
        if (teachers.status === 'fulfilled')
          setAllTeachers(teachers.value.data || []);
        if (archived.status === 'fulfilled')
          setAllArchivedStudents(archived.value?.data || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [data, navigate]);

  const stats = useMemo(() => {
    const missingTeacher = allStudents.filter(
      (s) => !getTeacherUsername(s),
    ).length;
    const noLevel = allStudents.filter((s) => s.level == null).length;
    const reportsSent = allStudents.filter(
      (s) => s.lastParentReportEmailSent,
    ).length;
    const studentsPerTeacher = allTeachers.map((t) => ({
      ...t,
      studentCount: allStudents.filter(
        (s) =>
          s.teacher?.id === t.id || s.teacherUserName === t.username,
      ).length,
    }));

    return {
      totalStudents: allStudents.length,
      totalTeachers: allTeachers.length,
      archived: allArchivedStudents.length,
      missingTeacher,
      noLevel,
      reportsSent,
      studentsPerTeacher,
    };
  }, [allStudents, allTeachers, allArchivedStudents]);

  const filteredStudents = useMemo(() => {
    const q = query.toLowerCase().trim();
    return allStudents.filter((s) => {
      const matchesQuery =
        !q ||
        [
          s.id,
          s.firstName,
          s.lastName,
          s.firstNameArabic,
          s.lastNameArabic,
          getTeacherUsername(s),
          getTeacherName(s),
        ]
          .join(' ')
          .toLowerCase()
          .includes(q);
      const matchesLevel =
        levelFilter === 'all' || String(s.level) === levelFilter;
      return matchesQuery && matchesLevel;
    });
  }, [allStudents, query, levelFilter]);

  const [showTabs, setShowTabs] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // At top of page => hide floating menu
      if (currentScrollY < 150) {
        setShowTabs(false);
      }
      // Scrolling up => show menu
      else if (currentScrollY < lastScrollY.current) {
        setShowTabs(true);
      }
      // Scrolling down => hide menu
      else {
        setShowTabs(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!allTeachers?.length) return;

    const fetchLastMonthAttendance = async () => {
      const end = new Date();
      const start = new Date();
      start.setMonth(end.getMonth() - 1);

      const formatDate = (date) => date.toISOString().split('T')[0];

      const startDate = formatDate(start);
      const endDate = formatDate(end);

      setAttendanceRange({
        start: startDate,
        end: endDate,
      });

      try {
        const teacherResults = [];
        const studentResults = [];

        await Promise.all(
          allTeachers.map(async (teacher) => {
            const response =
              await SchoolServiceAdmin.getMyStudentsOnDateRange(
                formatDate(start),
                formatDate(end),
                teacher.id,
              );

            let classPresentCount = 0;
            const studentMap = {};

            response.data?.forEach((studentRecords) => {
              studentRecords?.forEach((record) => {
                const student = record.studentEntity;

                if (!student) return;

                const studentId = student.id;
                const studentName = `${student.firstName || ''} ${
                  student.lastName || ''
                }`;

                if (!studentMap[studentId]) {
                  studentMap[studentId] = {
                    id: studentId,
                    studentName,
                    teacherName: `${teacher.firstName || ''} ${
                      teacher.lastName || ''
                    }`,
                    classAbbrev: teacher.classAbbrev || '-',
                    teacherUsername: teacher.username || '',
                    presentCount: 0,
                  };
                }

                if (record.present) {
                  classPresentCount += 1;
                  studentMap[studentId].presentCount += 1;
                }
              });
            });

            teacherResults.push({
              id: teacher.id,
              teacherName: `${teacher.firstName || ''} ${
                teacher.lastName || ''
              }`,
              classAbbrev: teacher.classAbbrev || '-',
              presentCount: classPresentCount,
            });

            studentResults.push(...Object.values(studentMap));
          }),
        );

        setTeacherAttendanceStats(
          teacherResults.sort(
            (a, b) => b.presentCount - a.presentCount,
          ),
        );

        setStudentAttendanceStats(
          studentResults.sort(
            (a, b) => b.presentCount - a.presentCount,
          ),
        );
      } catch (error) {
        console.error('Failed to fetch attendance stats:', error);
      }
    };

    fetchLastMonthAttendance();
  }, [allTeachers]);

  if (loading) {
    return (
      <div className="min-h-[70vh] grid place-items-center text-cyan-800 font-bold text-xl">
        Loading admin command center...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-1 py-4 sm:px-6 lg:px-10">
      <section className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="relative bg-gradient-to-br from-cyan-900 via-cyan-800 to-slate-900 p-6 text-white sm:p-10">
          <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 left-10 h-28 w-28 rounded-full bg-cyan-300/20 blur-2xl" />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-200">
                School Admin
              </p>
              <h1 className="mt-3 text-3xl font-black sm:text-5xl">
                Live Monitoring Center
              </h1>
              <p className="mt-3 max-w-2xl text-cyan-50/80">
                Students, teachers, reports, missing data, and
                attendance checks in one responsive dashboard.
              </p>
            </div>
            <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-md ring-1 ring-white/20">
              <p className="text-xs uppercase tracking-widest text-cyan-100">
                Next parent report
              </p>
              <p className="mt-1 text-lg font-bold">
                {getNextBiWeeklyReportDate()}
              </p>
              <p className="text-sm text-cyan-100">5:00 PM</p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6 lg:p-6">
          {[
            [
              'Students',
              stats.totalStudents,
              'bg-cyan-50 text-cyan-800',
            ],
            [
              'Teachers',
              stats.totalTeachers,
              'bg-indigo-50 text-indigo-800',
            ],
            [
              'Archived',
              stats.archived,
              'bg-slate-100 text-slate-700',
            ],
            [
              'Reports sent',
              stats.reportsSent,
              'bg-emerald-50 text-emerald-800',
            ],
          ].map(([label, value, cls]) => (
            <div
              key={label}
              className={`rounded-2xl p-4 shadow-sm ring-1 ring-black/5 ${cls}`}
            >
              <p className="text-xs font-bold uppercase tracking-wider opacity-70">
                {label}
              </p>
              <p className="mt-2 text-3xl font-black">{value}</p>
            </div>
          ))}
        </div>
        <div className="my-10">
          <div className="h-1 rounded-full bg-gradient-to-r from-cyan-700 via-cyan-500 to-teal-400 shadow-md shadow-cyan-500/30" />
        </div>
        <div
          className={`fixed left-0 right-0 top-0 z-[999] border-b border-cyan-100 bg-white/90 px-3 py-3 shadow-xl shadow-cyan-950/10 backdrop-blur-xl transition-all duration-300 sm:left-4 sm:right-4 sm:top-3 sm:rounded-3xl sm:border ${
            showTabs
              ? 'translate-y-0 opacity-100'
              : '-translate-y-full opacity-0'
          }`}
        >
          {' '}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              {
                id: 'overview',
                label: 'Overview',
                icon: '✦',
                hint: 'Control center',
              },
              {
                id: 'students',
                label: 'Students',
                icon: '🎓',
                hint: `${allStudents?.length || 0} learners`,
              },
              {
                id: 'teachers',
                label: 'Teachers',
                icon: '👩‍🏫',
                hint: `${allTeachers?.length || 0} mentors`,
              },
              {
                id: 'attendance',
                label: 'Attendance',
                icon: '✓',
                hint: 'Daily checks',
              },
            ].map((tab) => {
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);

                    window.scrollTo({
                      top: 100,
                      behavior: 'smooth',
                    });
                  }}
                  className={`group relative overflow-hidden rounded-2xl border px-4 py-4 text-left transition-all duration-300 sm:px-5 sm:py-5 ${
                    isActive
                      ? 'border-cyan-500 bg-gradient-to-br from-cyan-700 via-cyan-600 to-teal-500 text-white shadow-xl shadow-cyan-700/25 scale-[1.02]'
                      : 'border-slate-200 bg-white text-slate-700 shadow-sm hover:-translate-y-1 hover:border-cyan-300 hover:shadow-lg hover:shadow-cyan-900/10'
                  }`}
                >
                  <div
                    className={`absolute -right-8 -top-8 h-24 w-24 rounded-full transition-all duration-300 ${
                      isActive
                        ? 'bg-white/20'
                        : 'bg-cyan-100/60 group-hover:bg-cyan-200/70'
                    }`}
                  />

                  <div className="relative flex items-center gap-3">
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl text-2xl transition-all duration-300 ${
                        isActive
                          ? 'bg-white/20 shadow-inner'
                          : 'bg-cyan-50 group-hover:bg-cyan-100'
                      }`}
                    >
                      {tab.icon}
                    </div>

                    <div>
                      <p className="text-base font-black sm:text-lg">
                        {tab.label}
                      </p>
                      <p
                        className={`mt-0.5 text-xs font-semibold ${
                          isActive ? 'text-cyan-50' : 'text-slate-400'
                        }`}
                      >
                        {tab.hint}
                      </p>
                    </div>
                  </div>

                  {isActive && (
                    <div className="absolute bottom-0 left-6 right-6 h-1 rounded-t-full bg-white/90" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <main className="p-4 lg:p-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="rounded-3xl bg-white shadow-sm lg:col-span-2">
                  <h2 className="text-xl font-black text-slate-900">
                    Teacher workload
                  </h2>
                  <div className="mt-5 space-y-4">
                    {stats.studentsPerTeacher.map((t) => {
                      const mood = getTeacherLoadMood(t.studentCount);
                      return (
                        <div
                          key={t.id}
                          className={`rounded-3xl bg-slate-50 p-4 ring-1 ${mood.ring}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="font-bold text-slate-900">
                                {t.firstName} {t.lastName}
                              </p>
                              <p className="text-sm text-slate-500">
                                @{t.username} •{' '}
                                {daysText(t.teacherClassDaysOfWeek) ||
                                  'No days selected'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-4xl font-black leading-none text-cyan-800">
                                {t.studentCount}
                              </p>
                              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                                students
                              </p>
                            </div>
                          </div>
                          <StudentCountLines count={t.studentCount} />
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-black ${mood.chip}`}
                            >
                              {mood.label}
                            </span>
                            <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-500 ring-1 ring-slate-200">
                              Class {t.classAbbrev || '-'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-3xl border bg-gradient-to-b from-white to-cyan-50 p-5 shadow-sm">
                  <h2 className="text-xl font-black text-slate-900">
                    Admin alerts
                  </h2>
                  <div className="mt-5 space-y-3">
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-900">
                      <b>{stats.noLevel}</b> students without selected
                      level.
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
                      <b>{stats.reportsSent}</b> students have
                      parent-report history.
                    </div>
                  </div>
                </div>
              </div>
              {teacherAttendanceStats.length > 0 && (
                <div className="mt-6">
                  <TeacherAttendanceMonthlyChart
                    data={teacherAttendanceStats}
                    attendanceRange={attendanceRange}
                  />
                </div>
              )}

              {studentAttendanceStats.length > 0 && (
                <div className="mt-6">
                  <StudentAttendanceByClassChart
                    data={studentAttendanceStats}
                    attendanceRange={attendanceRange}
                  />
                </div>
              )}
            </>
          )}

          {activeTab === 'students' && (
            <div className="rounded-3xl border bg-white shadow-sm">
              <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
                <h2 className="text-xl font-black text-slate-900">
                  Students directory
                </h2>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search name, ID, teacher..."
                    className="rounded-2xl border px-4 py-2 outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <select
                    value={levelFilter}
                    onChange={(e) => setLevelFilter(e.target.value)}
                    className="rounded-2xl border px-4 py-2 outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="all">All levels</option>
                    {[
                      ...new Set(
                        allStudents
                          .map((s) => s.level)
                          .filter((v) => v != null),
                      ),
                    ].map((level) => (
                      <option key={level} value={level}>
                        {levelTranslations[level] || level}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      {[
                        '#',
                        'ID',
                        'Student',
                        'Arabic Name',
                        'Level',
                        'Teacher',
                        'Class Days',
                        'Last Report',
                        'Flags',
                      ].map((h) => (
                        <th key={h} className="px-4 py-3 text-left">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((s, index) => (
                      <tr
                        key={s.id}
                        className="border-b hover:bg-cyan-50/50"
                      >
                        <td className="px-4 py-3 font-bold text-slate-400">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3 font-bold">
                          {s.id}
                        </td>
                        <td className="px-4 py-3">
                          <b>
                            {s.firstName} {s.lastName}
                          </b>
                        </td>
                        <td className="px-4 py-3">
                          {s.firstNameArabic} {s.lastNameArabic}
                        </td>
                        <td
                          className={`px-4 py-3 font-bold text-white ${s.level != null ? levelColors[s.level] : 'bg-slate-600'}`}
                        >
                          {s.level != null
                            ? levelTranslations[s.level]
                            : 'Not selected'}
                        </td>
                        <td className="px-4 py-3">
                          {getTeacherUsername(s)}
                          <div className="text-xs text-slate-500">
                            {getTeacherName(s)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {daysText(getStudentDays(s))}
                        </td>
                        <td className="px-4 py-3">
                          {formatDate(s.lastParentReportEmailSent)}
                        </td>
                        <td className="px-4 py-3">
                          {!getTeacherUsername(s) && (
                            <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800">
                              No teacher
                            </span>
                          )}{' '}
                          {s.level == null && (
                            <span className="rounded-full bg-rose-100 px-2 py-1 text-xs font-bold text-rose-800">
                              No level
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 p-3 lg:hidden">
                {filteredStudents.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-2xl border bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-lg font-black">
                          {s.firstName} {s.lastName}
                        </p>
                        <p className="text-slate-500">
                          #{s.id} • {s.firstNameArabic}{' '}
                          {s.lastNameArabic}
                        </p>
                      </div>
                      <span
                        className={`rounded-xl px-3 py-1 text-xs font-bold text-white ${s.level != null ? levelColors[s.level] : 'bg-slate-600'}`}
                      >
                        {s.level != null
                          ? levelTranslations[s.level]
                          : 'No level'}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-slate-700">
                      <p>
                        <b>Teacher:</b>{' '}
                        {getTeacherUsername(s) || 'Missing'}{' '}
                        {getTeacherName(s) &&
                          `(${getTeacherName(s)})`}
                      </p>
                      <p>
                        <b>Days:</b>{' '}
                        {daysText(getStudentDays(s)) || '-'}
                      </p>
                      <p>
                        <b>Last report:</b>{' '}
                        {formatDate(s.lastParentReportEmailSent)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'teachers' && (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {stats.studentsPerTeacher.map((t) => {
                const mood = getTeacherLoadMood(t.studentCount);
                return (
                  <article
                    key={t.id}
                    className={`group rounded-3xl border bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${mood.ring}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-cyan-800 text-xl font-black text-white">
                        {t.firstName?.[0]}
                        {t.lastName?.[0]}
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-slate-900">
                          {t.firstName} {t.lastName}
                        </h3>
                        <p className="text-sm text-slate-500">
                          @{t.username}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 rounded-[1.5rem] bg-gradient-to-br from-cyan-50 to-white p-4 ring-1 ring-cyan-100">
                      <div className="flex items-end justify-between gap-3">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.25em] text-cyan-700/70">
                            Assigned students
                          </p>
                          <p className="mt-1 text-5xl font-black text-cyan-900">
                            {t.studentCount}
                          </p>
                        </div>
                        <span
                          className={`mb-2 rounded-full px-3 py-1 text-xs font-black ${mood.chip}`}
                        >
                          {mood.label}
                        </span>
                      </div>
                      <StudentCountLines count={t.studentCount} />
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                      <div className="rounded-2xl bg-slate-50 p-3">
                        <p className="text-slate-500">Teacher ID</p>
                        <b>{t.id}</b>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-3">
                        <p className="text-slate-500">Class</p>
                        <b>{t.classAbbrev || '-'}</b>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-3">
                        <p className="text-slate-500">Unique #</p>
                        <b>{t.classUniqueNumber || '-'}</b>
                      </div>
                      <div className="rounded-2xl bg-slate-50 p-3">
                        <p className="text-slate-500">Days</p>
                        <b>
                          {daysText(t.teacherClassDaysOfWeek) || '-'}
                        </b>
                      </div>
                    </div>
                    <p className="mt-4 break-words text-sm text-slate-600">
                      <b>Email:</b> {t.email}
                    </p>
                  </article>
                );
              })}
            </div>
          )}

          {activeTab === 'attendance' && (
            <AdminDisplayTeacherAttendanceCheck />
          )}
        </main>
      </section>
    </div>
  );
};

export default SchoolAdmin;
